/**
 * Rebuild a projection so it costs what its state costs (RFC 0357 4).
 *
 * A projection is a document whose history nobody will ever read: the project
 * registry is a catalogue, every fact in it derivable from the documents it
 * points at. Automerge keeps every op, so this cannot be done in place. A
 * rebuild writes a NEW document holding the current state and nothing else.
 *
 * A REBUILD ON ITS OWN IS NEARLY WORTHLESS. Measured against the registry that
 * put an 8 GB laptop into "out of application memory":
 *
 *     ops today                            762,704
 *     rebuilt, plainly                     534,875    1.4x
 *     rebuilt, with the conversion          35,678   21.4x
 *
 * Writing a document from current state writes every string as a TEXT object
 * again, one op per character, because that is what Automerge does with a plain
 * string. Nearly a megabyte of that registry is text. So the rebuild applies
 * RFC 0356's conversion as it writes: a string that is not prose is stored as
 * one op, not one per character.
 */

import * as A from '@automerge/automerge/slim'

/**
 * Keys whose value a person edits as text, which must stay text.
 *
 * Everything else on a projection is an id, a path, a label or a timestamp:
 * replaced whole when it changes, never typed into together.
 */
const PROSE_KEYS: ReadonlySet<string> = new Set([
  'body',
  'content',
  'text',
  'description',
  'instructions',
])

/** The characters of a stored string, whether or not it is wrapped. */
function plainOf(v: unknown): unknown {
  if (v instanceof A.ImmutableString) return String(v)
  if (Array.isArray(v)) return v.map(plainOf)
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      out[k] = plainOf(x)
    }
    return out
  }
  return v
}

/** The same value, with every non-prose string stored as one op. */
function scalarise(v: unknown, key: string): unknown {
  if (typeof v === 'string') {
    return PROSE_KEYS.has(key) ? v : new A.ImmutableString(v)
  }
  if (Array.isArray(v)) return v.map((x) => scalarise(x, key))
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      out[k] = scalarise(x, k)
    }
    return out
  }
  return v
}

/** The state a document holds, with no wrappers and no history. */
export function stateOf<T>(doc: T): unknown {
  return plainOf(doc)
}

/**
 * A new document holding `doc`'s state, written the cheap way.
 *
 * The result is a DIFFERENT DOCUMENT. It shares no history with the original
 * and will not merge with it: the caller has to repoint whatever named the old
 * one, which is the real cost of this and the reason RFC 0357 has a slice for
 * it rather than a function call.
 */
export function rebuildProjection<T>(doc: T): T {
  return A.from(scalarise(plainOf(doc), '') as Record<string, unknown>) as T
}

/** Ops in a document's whole history: what it costs to hold. */
export function opCount(doc: unknown): number {
  let n = 0
  for (const change of A.getAllChanges(doc as never)) {
    n += A.decodeChange(change).ops.length
  }
  return n
}

/**
 * Two states, compared the way that matters.
 *
 * IGNORING KEY ORDER, because a rebuilt document inserts keys in its own order
 * and that is not a difference in what it says. Comparing without this said
 * "different" three times on a registry where no entry differed at all.
 */
export function sameState(a: unknown, b: unknown): boolean {
  const canon = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(canon)
    if (v && typeof v === 'object') {
      const o: Record<string, unknown> = {}
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        o[k] = canon((v as Record<string, unknown>)[k])
      }
      return o
    }
    return v
  }
  return JSON.stringify(canon(plainOf(a))) === JSON.stringify(canon(plainOf(b)))
}
